import {
  crateIcon,
  crossIcon,
  footprintIcon,
  grassIcon,
  hedgeIcon,
  homeIcon,
  pitchforkIcon,
  raindropsIcon,
  trunkIcon,
} from "@/icons";
import { CellType } from "@/types";
import type { SunExposure } from "@/types";
import { TREE_CANOPY_DIAMETER_RATIO } from "./structures";

/**
 * Cell type display configuration interface
 */
export interface CellTypeDisplayConfig {
  label: string;
  color: string;
}

// Structure/greenhouse/bed heights are stored in cm on the cell but edited in meters
// in the UI — the single place that unit conversion lives (both directions).
export const metersToCm = (meters: number): number => Math.round(meters * 100);
export const cmToMeters = (cm: number): number => cm / 100;

// The backend's optimiser scores are stored ×100; everything shown (the plan cell
// badge and the Score tool chart) divides by this to display a human value.
export const SCORE_DISPLAY_DIVISOR = 100;
export const scaleScore = (raw: number): number => raw / SCORE_DISPLAY_DIVISOR;

/** Single source of truth for the icon of each cell type (used by the cell
 * renderer and the brush palette). */
export const CELL_TYPE_ICON: Record<CellType, string> = {
  [CellType.FREE]: pitchforkIcon,
  [CellType.CROP]: pitchforkIcon,
  [CellType.PATH]: footprintIcon,
  [CellType.OBSTACLE]: crateIcon,
  [CellType.WATER]: raindropsIcon,
  [CellType.GRASS]: grassIcon,
  [CellType.BUILDING]: homeIcon,
  [CellType.TREE]: trunkIcon,
  [CellType.HEDGE]: hedgeIcon,
  [CellType.OUT]: crossIcon,
};

// Tile background colour per cell type — single source of truth for the plan's 2D
// palette. Mirrored by Cell.vue's `.bg-*` SCSS (which keeps its own hover gradients),
// the 3D ground tiles (`useGarden3D`), and the brush palette legend (`BrushTool`).
// Keep all consumers in sync. OUT has no fill (rendered transparent / no 3D tile).
export const CELL_TYPE_COLOR: Record<CellType, string> = {
  [CellType.FREE]: "#e8dab2", // light warm tan — the cultivable "canvas", makes crop icons pop
  [CellType.CROP]: "#e8dab2",
  [CellType.PATH]: "#c3a56f", // darker walkway brown, so the beds stand out
  [CellType.OBSTACLE]: "#c9c0a2", // taupe — a structure, distinct from cultivable soil
  [CellType.WATER]: "#a8c5de",
  [CellType.GRASS]: "#c5d59c",
  [CellType.BUILDING]: "#82807a", // warm asphalt/road grey (less "sad" than blue-grey)
  [CellType.TREE]: "#8fb06a",
  [CellType.HEDGE]: "#6f9458", // deeper hedge green, separated from the tree canopy
  [CellType.OUT]: "transparent",
};

/**
 * Cell type display configuration - single source of truth for cell rendering metadata
 */
export const CELL_TYPE_DISPLAY: Record<CellType, CellTypeDisplayConfig> = {
  [CellType.FREE]: {
    label: "Terre libre",
    color: "success",
  },
  [CellType.CROP]: {
    label: "Culture plantée",
    color: "success",
  },
  [CellType.PATH]: {
    label: "Chemin",
    color: "grey",
  },
  [CellType.OBSTACLE]: {
    label: "Obstacle",
    color: "warning",
  },
  [CellType.WATER]: {
    label: "Point d'eau",
    color: "blue",
  },
  [CellType.GRASS]: {
    label: "Pelouse",
    color: "green",
  },
  [CellType.BUILDING]: {
    label: "Bâtiment",
    color: "grey-darken-2",
  },
  [CellType.TREE]: {
    label: "Arbre",
    color: "green-darken-2",
  },
  [CellType.HEDGE]: {
    label: "Haie",
    color: "green-darken-2",
  },
  [CellType.OUT]: {
    label: "Hors limite",
    color: "error",
  },
};

// Cell types that carry a `height` and cast shade — the single source of truth
// for "does this cell have a height?" (feeds the backend's sun-exposure model).
// NB: this is the whole shade set; the brush palette edits OBSTACLE/TREE/HEDGE via
// a height preset, while BUILDING derives its height from a type × floor count.
export const SHADE_TYPES: readonly CellType[] = [CellType.BUILDING, CellType.OBSTACLE, CellType.TREE, CellType.HEDGE];

// A dated crop placement in a cell (a crop + its occupation interval). Cells can
// hold several successive placements over time (spring sowing then autumn crop on
// the same spot). Dates come from the crop; `endOn` is null for an ongoing perennial.
export interface Placement {
  // Backend placement id (present for persisted placements; absent for freshly
  // painted, not-yet-saved ones). Needed to DELETE/PATCH via /placements/.
  id?: number;
  cropId: number;
  startOn: string | null;
  endOn: string | null;
  // Whether this placement is locked (the optimiser won't move it). Lock lives on
  // the placement, not the cell — you lock a placed crop, not an empty parcel.
  isLocked: boolean;
  // Optimiser scores (criterion → number, plus `total`), read-only. Lives on the
  // placement now (it depends on the crop + period). `{}`/absent until optimised.
  scores?: Record<string, number>;
  // Id grouping the cells of a single plant (specimen) that spans several contiguous
  // cells. Cells sharing a non-null `specimenId` are the same plant and render as one
  // merged block; `null`/absent = a hand-placed or anchored cell → an isolated cell.
  // Display-only and recomputed on each optimisation — never use as a stable key.
  specimenId?: number | null;
}

export default class Cell {
  type: CellType;
  // Backend cell id (structure). Null for a freshly added cell (resize) before the
  // structure is saved. Placements reference the cell by this id.
  id: number | null = null;
  // Source of truth for the cell's crops. Most cells have 0 or 1 placement, but the
  // model supports successions. The single-crop UI still reads/writes via the
  // `cropId` accessor below (= the first placement); `cropIds` is the full list.
  placements: Placement[] = [];
  hoverCell: Cell | null;
  // Set on a hover-PREVIEW cell to suppress the green "edit here" tint (.cell-hover-state).
  // Used when a preview merely HIDES a crop as a side effect (e.g. a multi-cell specimen
  // the plant tool would overwrite) rather than marking the cell as the active target.
  suppressHoverTint = false;
  // Set on a hover-PREVIEW cell when the plant footprint can't be placed here (any of its
  // cells is out of bounds / non-cultivable). Marks the WHOLE footprint with an error
  // state (red wash + not-allowed cursor), not just the anchor cell.
  invalidPlacement = false;
  // undefined = "no override" (used by hover-preview cells whose brush
  // doesn't touch the greenhouse); null = explicit "no greenhouse";
  // number = greenhouse with that height.
  greenhouseHeight: number | null | undefined;
  // Structure height in cm for BUILDING/OBSTACLE/TREE/HEDGE cells (used by the
  // backend for sun-exposure). Same undefined/null/number semantics as greenhouseHeight.
  height: number | null | undefined;
  // Read-only sun-exposure per season, computed by the backend on save.
  sunExposure: SunExposure;

  constructor(
    type: CellType = CellType.FREE,
    cropId: number | null = null,
    greenhouseHeight?: number | null,
    height?: number | null,
    sunExposure: SunExposure = {}
  ) {
    this.type = type;
    this.cropId = cropId; // via the accessor → placements
    this.hoverCell = null;
    this.greenhouseHeight = greenhouseHeight;
    this.height = height;
    this.sunExposure = sunExposure;
  }

  // Backward-compatible single-crop view: the first placed crop. Setting it
  // replaces the whole list with a single (dateless, unlocked) placement — the
  // current single-crop editing (paint/erase) goes through here.
  get cropId(): number | null {
    return this.placements[0]?.cropId ?? null;
  }
  set cropId(value: number | null) {
    this.placements = value == null ? [] : [{ cropId: value, startOn: null, endOn: null, isLocked: false }];
  }

  // All placed crop ids (write model → `crop_ids` on save).
  get cropIds(): number[] {
    return this.placements.map((p) => p.cropId);
  }

  // Single-crop view of the specimen (the first placement's), to decide block
  // merging: adjacent cells sharing a non-null specimen are the same plant. `null`
  // for a hand-placed/anchored cell → rendered isolated, never merged.
  get specimenId(): number | null {
    return this.placements[0]?.specimenId ?? null;
  }

  // Builds a shade structure cell (building/obstacle/tree/hedge) from a height in
  // meters. Explicit `null` greenhouse so painting a structure clears any greenhouse
  // the cell carried.
  static structure(type: CellType, heightMeters: number): Cell {
    console.assert(SHADE_TYPES.includes(type), `Cell.structure called with a non-shade type: ${type}`);
    return new Cell(type, null, null, metersToCm(heightMeters));
  }

  // Builds a plain terrain cell (path/water/grass/out) with no crop, greenhouse or
  // height — the explicit `null`s reset those when painting over a cell that had
  // them (undefined would mean "leave unchanged", see `set`).
  static terrain(type: CellType): Cell {
    return new Cell(type, null, null, null);
  }

  get isArable(): boolean {
    return [CellType.FREE, CellType.CROP].includes(this.type);
  }

  // Carries a height and casts shade (building, obstacle, tree, hedge).
  get castsShade(): boolean {
    return SHADE_TYPES.includes(this.type);
  }

  // A cultivable cell raised into a bed (FREE/CROP ground carrying a height).
  get isRaisedBed(): boolean {
    return this.isArable && !!this.height;
  }

  // Structure/bed height in meters (null when the cell has none) — the read side of
  // the cm↔meters conversion; the 2D plan, 3D view and canopy geometry read through it.
  get heightMeters(): number | null {
    return this.height ? cmToMeters(this.height) : null;
  }

  // Greenhouse height in meters (null when the cell has no greenhouse).
  get greenhouseHeightMeters(): number | null {
    return this.greenhouseHeight ? cmToMeters(this.greenhouseHeight) : null;
  }

  // A tree's foliage-disc diameter in meters (canopy ratio × height); 0 for a
  // non-tree cell or a tree with no height.
  get canopyDiameterMeters(): number {
    if (this.type !== CellType.TREE || !this.heightMeters) return 0;
    return TREE_CANOPY_DIAMETER_RATIO * this.heightMeters;
  }

  get isOptimizable(): boolean {
    return this.type === CellType.FREE;
  }

  // Cells that count toward the garden's total surface area (the plot's extent,
  // shown on the home preview). Excludes building footprints and out-of-bounds
  // zones only — trees/hedges still sit on your ground. NB: this is not the same
  // as *cultivable* ground (that's `isArable`).
  get isGardenSurface(): boolean {
    return ![CellType.BUILDING, CellType.OUT].includes(this.type);
  }

  equals(cell: Cell | null): boolean {
    if (!cell) return false;
    if (this.type !== cell.type) return false;
    const a = this.cropIds;
    const b = cell.cropIds;
    return a.length === b.length && a.every((id, i) => id === b[i]);
  }

  set(cell: Cell): void {
    // Update the cell attributes to equal the given cell.
    console.assert(!cell.cropId || cell.type === CellType.CROP, `Warning: setting cropId with cell type ${cell.type}`);
    this.type = cell.type;
    this.placements = cell.placements.map((p) => ({ ...p }));
    this.greenhouseHeight = cell.greenhouseHeight === undefined ? this.greenhouseHeight : cell.greenhouseHeight;
    this.height = cell.height === undefined ? this.height : cell.height;
    this.hoverCell = null;
  }

  swapCrop(cell: Cell): void {
    const placements = cell.placements;
    cell.placements = this.placements;
    this.placements = placements;
  }

  setHoverCell(cell: Cell): void {
    this.hoverCell = cell;
  }

  resetHover(): void {
    this.hoverCell = null;
  }
}
